import ParticleLoader from "../ParticleLoader";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ChatBubbleLeftRightIcon,
    ClipboardDocumentIcon,
    EnvelopeIcon,
    PencilSquareIcon,
    PlayIcon,
    PlusIcon,
    TrashIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import QuestionnaireBuilderModal from "./QuestionnaireBuilderModal";
import QuestionnaireResponsesModal from "./QuestionnaireResponsesModal";
import { questionnaireError } from "./questionnaireErrors";
import {
    listQuestionnaires,
    saveQuestionnaire,
    deleteQuestionnaire,
    setQuestionnaireStatus,
    processQuestionnaire,
    inviteParticipants,
} from "../../Requests/QuestionnaireRequests";

/**
 * Participants are pasted in, so anything a person would plausibly separate
 * them with counts: newlines, commas, semicolons or spaces.
 */
export function parseEmailList(raw) {
    const seen = new Set();
    const valid = [];
    const invalid = [];
    (raw || "")
        .split(/[\s,;]+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((candidate) => {
            const key = candidate.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) valid.push(candidate);
            else invalid.push(candidate);
        });
    return { valid, invalid };
}

/**
 * Data Collection tab: build questionnaires, share the public link, and turn the
 * collected answers into an analysis.
 *
 * Processing is explicit rather than automatic — a public link that anyone can
 * submit to must never be able to spend team credits on its own.
 */
export default function QuestionnaireTab({ projectUuid }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [questionnaires, setQuestionnaires] = useState(null);
    const [editing, setEditing] = useState(null);
    const [viewingResponses, setViewingResponses] = useState(null);
    const [busyUuid, setBusyUuid] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(null);
    const [notice, setNotice] = useState(null);
    const [inviting, setInviting] = useState(null);
    const [inviteText, setInviteText] = useState("");

    const load = useCallback(() => {
        listQuestionnaires(projectUuid)
            .then((res) => setQuestionnaires(res.data))
            .catch(() => setQuestionnaires([]));
    }, [projectUuid]);

    useEffect(() => {
        load();
    }, [load]);

    const create = async () => {
        setError(null);
        const res = await saveQuestionnaire(projectUuid, {
            title: t("Questionnaire.Untitled", "Untitled questionnaire"),
        });
        await load();
        setEditing(res.data.uuid);
    };

    const toggleStatus = async (questionnaire) => {
        setBusyUuid(questionnaire.uuid);
        setError(null);
        try {
            await setQuestionnaireStatus(
                projectUuid,
                questionnaire.uuid,
                questionnaire.status === "active" ? "draft" : "active"
            );
            await load();
        } catch (e) {
            // The consent and questions gates land here — surface the reason
            // rather than silently failing to publish.
            setError(questionnaireError(e, t));
        } finally {
            setBusyUuid(null);
        }
    };

    const remove = async (questionnaire) => {
        setBusyUuid(questionnaire.uuid);
        try {
            await deleteQuestionnaire(projectUuid, questionnaire.uuid);
            await load();
        } finally {
            setBusyUuid(null);
        }
    };

    const process = async (questionnaire) => {
        setBusyUuid(questionnaire.uuid);
        setError(null);
        setNotice(null);
        try {
            const res = await processQuestionnaire(projectUuid, questionnaire.uuid);
            setNotice(
                res.data.transcribing > 0
                    ? t(
                        "Questionnaire.ProcessStartedWithVoice",
                        "Transcribing {{v}} recording(s), then analysing {{n}} participant(s).",
                        { v: res.data.transcribing, n: res.data.participants }
                    )
                    : t("Questionnaire.ProcessStarted", "Analysing {{n}} participant(s) — one analysis each.", {
                        n: res.data.participants,
                    })
            );
            await load();
        } catch (e) {
            setError(questionnaireError(e, t, "Questionnaire.ProcessFailed"));
        } finally {
            setBusyUuid(null);
        }
    };

    const sendInvites = async (questionnaire) => {
        const { valid, invalid } = parseEmailList(inviteText);
        setError(null);
        setNotice(null);

        if (invalid.length > 0) {
            setError(t("Questionnaire.InviteInvalid", "Not a valid email: {{list}}", { list: invalid.join(", ") }));
            return;
        }
        if (valid.length === 0) {
            setError(t("Questionnaire.InviteEmpty", "Add at least one email address."));
            return;
        }

        setBusyUuid(questionnaire.uuid);
        try {
            const res = await inviteParticipants(projectUuid, questionnaire.uuid, valid);
            setNotice(
                t("Questionnaire.InviteSent", "Invitation sent to {{n}} participant(s).", { n: res.data.sent })
            );
            setInviting(null);
            setInviteText("");
        } catch (e) {
            setError(questionnaireError(e, t, "Questionnaire.InviteFailed"));
        } finally {
            setBusyUuid(null);
        }
    };

    const copyLink = (questionnaire) => {
        navigator.clipboard?.writeText(questionnaire.public_link);
        setCopied(questionnaire.uuid);
        setTimeout(() => setCopied(null), 2000);
    };

    if (questionnaires === null) {
        return <div className="py-8 flex justify-center"><ParticleLoader size={32} /></div>;
    }

    return (
        <div data-testid="detail-project-data-collection-tab" className="py-4">
            <div className="mb-4 flex items-center justify-end">
                <button
                    type="button"
                    onClick={create}
                    className="inline-flex flex-none items-center gap-1.5 rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                >
                    <PlusIcon className="h-4 w-4" />
                    {t("Questionnaire.New", "New questionnaire")}
                </button>
            </div>

            {error && (
                <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
            )}

            {notice && (
                <p className="mb-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>
            )}

            {questionnaires.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-12 text-center">
                    <p className="text-sm text-gray-500">
                        {t("Questionnaire.Empty", "No questionnaires yet.")}
                    </p>
                </div>
            ) : (
                <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {questionnaires.map((questionnaire) => (
                        <li
                            key={questionnaire.uuid}
                            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h3 className="truncate font-medium text-gray-900">{questionnaire.title}</h3>
                                    <p className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                                        <span
                                            className={`rounded-full px-2 py-0.5 font-medium ${
                                                questionnaire.status === "active"
                                                    ? "bg-green-100 text-green-800"
                                                    : "bg-gray-100 text-gray-700"
                                            }`}
                                        >
                                            {t(`Questionnaire.Status.${questionnaire.status}`, questionnaire.status)}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <UsersIcon className="h-3.5 w-3.5" />
                                            {questionnaire.completed_responses_count}
                                        </span>
                                        <span>
                                            {questionnaire.questions.length}{" "}
                                            {t("Questionnaire.QuestionsShort", "questions")}
                                        </span>
                                    </p>
                                </div>
                                <div className="flex flex-none gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setEditing(questionnaire.uuid)}
                                        className="p-1.5 text-gray-400 hover:text-gray-700"
                                        title={t("Generic.Edit", "Edit")}
                                    >
                                        <PencilSquareIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => remove(questionnaire)}
                                        disabled={busyUuid === questionnaire.uuid}
                                        className="p-1.5 text-gray-400 hover:text-red-600"
                                        title={t("Generic.Delete", "Delete")}
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            {!questionnaire.has_consent && (
                                <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                    {t(
                                        "Questionnaire.ConsentMissing",
                                        "Add informed consent text before publishing."
                                    )}
                                </p>
                            )}

                            {questionnaire.status === "active" && (
                                <button
                                    type="button"
                                    onClick={() => copyLink(questionnaire)}
                                    className="mt-3 flex w-full items-center gap-2 truncate rounded-md bg-gray-50 px-3 py-2 text-left text-xs text-gray-600 hover:bg-gray-100"
                                >
                                    <ClipboardDocumentIcon className="h-4 w-4 flex-none" />
                                    <span className="truncate">
                                        {copied === questionnaire.uuid
                                            ? t("Questionnaire.LinkCopied", "Link copied")
                                            : questionnaire.public_link}
                                    </span>
                                </button>
                            )}

                            {questionnaire.status === "active" && (
                                inviting === questionnaire.uuid ? (
                                    <div className="mt-2 rounded-md border border-gray-200 p-3">
                                        <label
                                            htmlFor={`invite-${questionnaire.uuid}`}
                                            className="block text-xs font-medium text-gray-700"
                                        >
                                            {t("Questionnaire.InviteLabel", "Participant emails")}
                                        </label>
                                        <textarea
                                            id={`invite-${questionnaire.uuid}`}
                                            rows={3}
                                            value={inviteText}
                                            onChange={(e) => setInviteText(e.target.value)}
                                            placeholder={t(
                                                "Questionnaire.InvitePlaceholder",
                                                "one@example.com, two@example.com"
                                            )}
                                            className="mt-1 block w-full rounded-md border-gray-300 text-xs shadow-sm focus:border-black focus:ring-black"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            {t(
                                                "Questionnaire.InviteHelp",
                                                "Separate addresses with a comma, a space or a new line. Each one gets the link to this questionnaire."
                                            )}
                                        </p>
                                        <div className="mt-2 flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => sendInvites(questionnaire)}
                                                disabled={busyUuid === questionnaire.uuid}
                                                className="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-40"
                                            >
                                                {t("Questionnaire.InviteSend", "Send invitations")}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setInviting(null);
                                                    setInviteText("");
                                                }}
                                                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                {t("Generic.Cancel", "Cancel")}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setInviting(questionnaire.uuid);
                                            setInviteText("");
                                            setError(null);
                                        }}
                                        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        <EnvelopeIcon className="h-3.5 w-3.5" />
                                        {t("Questionnaire.InviteByEmail", "Invite by email")}
                                    </button>
                                )
                            )}

                            <div className="mt-3 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => toggleStatus(questionnaire)}
                                    disabled={busyUuid === questionnaire.uuid}
                                    className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    {questionnaire.status === "active"
                                        ? t("Questionnaire.Unpublish", "Unpublish")
                                        : t("Questionnaire.Publish", "Publish")}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => process(questionnaire)}
                                    disabled={
                                        busyUuid === questionnaire.uuid ||
                                        questionnaire.completed_responses_count === 0
                                    }
                                    className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-40"
                                    title={t(
                                        "Questionnaire.ProcessHelp",
                                        "Build a transcript from the responses and run the analysis"
                                    )}
                                >
                                    <PlayIcon className="h-3.5 w-3.5" />
                                    {t("Questionnaire.Process", "Analyse responses")}
                                </button>

                                {questionnaire.responses_count > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setViewingResponses(questionnaire.uuid)}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                                        {t("Questionnaire.ViewResponses", "View responses")}
                                    </button>
                                )}

                                {questionnaire.analysed_count > 0 && (
                                    <span className="self-center text-xs text-gray-500">
                                        {t("Questionnaire.AnalysedCount", "{{n}} analysed", {
                                            n: questionnaire.analysed_count,
                                        })}
                                    </span>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {viewingResponses && (
                <QuestionnaireResponsesModal
                    projectUuid={projectUuid}
                    questionnaireUuid={viewingResponses}
                    onClose={() => setViewingResponses(null)}
                />
            )}

            {editing && (
                <QuestionnaireBuilderModal
                    projectUuid={projectUuid}
                    questionnaireUuid={editing}
                    onClose={() => {
                        setEditing(null);
                        load();
                    }}
                    onSaved={load}
                />
            )}
        </div>
    );
}
